import { useCallback, useEffect, useRef, useState } from 'react';
import { Sparkles, X, Send, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import { useQuery } from 'react-query';
import { techAssistantAPI, streamTechAssistantChat } from '@shared/apiClient';
import PendingActionCard from './PendingActionCard';

const WELCOME_MESSAGE = {
  role: 'assistant',
  content: "Hi! I'm your Tech Assistant. I can check your day, look up your calls and customers, log work on a call, request parts, and answer how-to questions about the app. What do you need?",
};

const EXAMPLE_PROMPTS = [
  "What's on my plate today?",
  'What needs my attention?',
  'Look up a customer',
  'Request a part',
  'How do I complete a call?',
];

export default function AssistantPanel({ open, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamContent, setStreamContent] = useState('');
  const [activeTool, setActiveTool] = useState(null);
  const [pendingActions, setPendingActions] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  const { data: statusData } = useQuery(
    'techAssistantStatus',
    () => techAssistantAPI.getStatus(),
    { enabled: open, staleTime: 60000 }
  );

  const configured = statusData?.data?.configured ?? true;

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamContent, pendingActions, scrollToBottom]);

  const sendMessage = async (text) => {
    const trimmed = text.trim();
    if (!trimmed || streaming) return;

    const userMessage = { role: 'user', content: trimmed };
    const history = [...messages, userMessage];
    setMessages(history);
    setInput('');
    setStreaming(true);
    setStreamContent('');
    setActiveTool(null);
    setPendingActions([]);

    const controller = new AbortController();
    abortRef.current = controller;

    let accumulated = '';

    try {
      await streamTechAssistantChat(
        history.map(({ role, content }) => ({ role, content })),
        {
          signal: controller.signal,
          onEvent: (event, data) => {
            switch (event) {
              case 'token':
                accumulated += data.content;
                setStreamContent(accumulated);
                break;
              case 'tool_status':
                setActiveTool(data.status === 'running' ? data.message : null);
                break;
              case 'pending_action':
                setPendingActions((prev) => [...prev, data]);
                break;
              case 'error':
                accumulated += `\n\nError: ${data.message}`;
                setStreamContent(accumulated);
                break;
              default:
                break;
            }
          }
        }
      );

      if (accumulated) {
        setMessages((prev) => [...prev, { role: 'assistant', content: accumulated }]);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        const errorMsg = err.message || 'Failed to get a response';
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: `Sorry, something went wrong: ${errorMsg}` }
        ]);
      }
    } finally {
      setStreaming(false);
      setStreamContent('');
      setActiveTool(null);
      abortRef.current = null;
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleActionConfirmed = (data) => {
    const note = `[Action completed: ${data.summary}]`;
    setMessages((prev) => [...prev, { role: 'user', content: note, hidden: true }]);
  };

  const handleClose = () => {
    abortRef.current?.abort();
    onClose();
  };

  if (!open) return null;

  const displayMessages = messages.length === 0 ? [WELCOME_MESSAGE] : messages;

  return (
    <div className="fixed inset-0 z-[90] flex justify-end">
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/40"
        aria-label="Close assistant"
        onClick={handleClose}
      />

      <div className="relative flex h-full w-full max-w-md flex-col border-l border-slate-200 bg-white shadow-xl">
        <header className="flex flex-shrink-0 items-center justify-between border-b border-slate-200 px-4 py-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary-600" />
            <div>
              <h2 className="text-sm font-semibold text-slate-900">Tech Assistant</h2>
              {!configured && (
                <p className="text-xs text-amber-600">Not configured — contact the office</p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">
          {displayMessages.map((msg, idx) => {
            if (msg.hidden) return null;
            const isUser = msg.role === 'user';
            return (
              <div key={idx} className={clsx('flex', isUser ? 'justify-end' : 'justify-start')}>
                <div
                  className={clsx(
                    'max-w-[85%] whitespace-pre-wrap rounded-2xl px-3 py-2 text-sm',
                    isUser
                      ? 'bg-primary-600 text-white'
                      : 'border border-slate-200 bg-slate-50 text-slate-800'
                  )}
                >
                  {msg.content}
                </div>
              </div>
            );
          })}

          {streaming && streamContent && (
            <div className="flex justify-start">
              <div className="max-w-[85%] whitespace-pre-wrap rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">
                {streamContent}
              </div>
            </div>
          )}

          {streaming && !streamContent && (
            <div className="flex justify-start">
              <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-600">
                <Loader2 className="h-3 w-3 animate-spin" />
                {activeTool || 'Thinking…'}
              </div>
            </div>
          )}

          {pendingActions.map((action) => (
            <PendingActionCard
              key={action.id}
              action={action}
              onConfirmed={handleActionConfirmed}
            />
          ))}

          <div ref={messagesEndRef} />
        </div>

        {messages.length === 0 && !streaming && (
          <div className="flex flex-shrink-0 flex-wrap gap-1.5 px-4 pb-2">
            {EXAMPLE_PROMPTS.map((prompt) => (
              <button
                key={prompt}
                type="button"
                onClick={() => sendMessage(prompt)}
                disabled={!configured}
                className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs text-slate-600 hover:bg-slate-100 disabled:opacity-50"
              >
                {prompt}
              </button>
            ))}
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex-shrink-0 border-t border-slate-200 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <div className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
              placeholder={configured ? 'Ask a question or log work…' : 'Assistant not configured'}
              disabled={streaming || !configured}
              rows={2}
              className="flex-1 resize-none rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={streaming || !input.trim() || !configured}
              className="flex-shrink-0 rounded-xl bg-primary-600 p-2.5 text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {streaming ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
            </button>
          </div>
          <p className="mt-1.5 text-xs text-slate-400">Actions require your confirmation before executing.</p>
        </form>
      </div>
    </div>
  );
}
